import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles } from 'lucide-react';

interface DiagramOption {
  id?: string | number;
  name?: string;
}

interface ContextHintsModalProps {
  open: boolean;
  onClose: () => void;
  onGenerate: (isaContext: string) => void;
  isGenerating: boolean;
  error?: string | null;
  initialContext?: string;
  diagrams?: DiagramOption[];
  selectedDiagramIndices?: Set<number>;
  onToggleDiagram?: (index: number, checked: boolean) => void;
}

export default function ContextHintsModal({ open, onClose, onGenerate, isGenerating, error, initialContext, diagrams, selectedDiagramIndices, onToggleDiagram }: ContextHintsModalProps) {
  const showDiagrams = diagrams && diagrams.length > 1 && selectedDiagramIndices && onToggleDiagram;
  const [text, setText] = useState('');

  useEffect(() => {
    if (open) setText(initialContext ?? '');
  }, [open, initialContext]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !isGenerating && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Context for Question Generation</DialogTitle>
          <DialogDescription>
            Describe system characteristics not visible in the diagram so the AI can generate more relevant questions (e.g. file uploads, PII handling, regulatory requirements, CI/CD pipeline, message queues…).
          </DialogDescription>
        </DialogHeader>

        {showDiagrams && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">
              Diagrams to include ({selectedDiagramIndices.size}/{diagrams.length} selected)
            </p>
            <div className="flex flex-wrap gap-x-5 gap-y-1.5">
              {diagrams.map((d, i) => (
                <label key={d.id ?? i} className="flex items-center gap-1.5 text-sm cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="rounded"
                    checked={selectedDiagramIndices.has(i)}
                    onChange={(e) => onToggleDiagram(i, e.target.checked)}
                    disabled={isGenerating}
                  />
                  {d.name || `Diagram ${i + 1}`}
                </label>
              ))}
            </div>
          </div>
        )}

        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="e.g. The system accepts file uploads from external users, processes PII subject to GDPR, uses Redis for caching, and has a CI/CD pipeline with automated SAST scanning…"
          rows={5}
          disabled={isGenerating}
        />

        {error && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="ghost" size="sm" onClick={() => onGenerate('')} disabled={isGenerating} className="sm:mr-auto">
            Skip
          </Button>
          <Button variant="outline" onClick={onClose} disabled={isGenerating}>
            Cancel
          </Button>
          <Button onClick={() => onGenerate(text.trim())} disabled={isGenerating}>
            <Sparkles className="mr-2 h-4 w-4" />
            {isGenerating ? 'Generating…' : 'Generate Questions'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
